import React, { useState, useCallback, useRef, useEffect } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { RichTextEditor } from "../RichTextEditor";
import { AutomatedControlData, AUTOMATED_CONTROL_TYPES } from "../types-automated";
import { Loader2 } from "lucide-react";
import { cn } from "../../../lib/utils";
import { MultiSelectDropdown } from "../../ui/multi-select-dropdown";

interface ControlSummaryAutomatedProps {
  data: AutomatedControlData;
  onChange: (data: Partial<AutomatedControlData>) => void;
  controlId?: string;
}

type PaneType = "control-details" | "related-accounts";

interface RelatedAccount {
  par: string;
  note_line_id: string;
  note_line: string;
  amount: number;
}

// Separate component for Related Accounts Table to isolate state
const RelatedAccountsTable: React.FC<{ workspaces: string[] }> = ({ workspaces }) => {
  const [accounts, setAccounts] = useState<RelatedAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLoad = async () => {
    if (!workspaces || workspaces.length === 0) {
      setError("No workspaces linked. Please link workspaces in the Control Details tab.");
      setAccounts([]);
      return;
    }

    setLoading(true);
    setError(null);
    setAccounts([]);

    try {
      if (window.sharePointAPI && typeof window.sharePointAPI.fetchRelatedAccounts === "function") {
        const result = await window.sharePointAPI.fetchRelatedAccounts(workspaces);

        if (result.success && result.data) {
          const rows = Array.isArray(result.data) ? result.data : [];
          const mappedAccounts: RelatedAccount[] = rows.map((item: any) => ({
            par: item.par || "",
            note_line_id: item.note_line_id || "",
            note_line: item.note_line || "",
            amount: typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0,
          }));
          setAccounts(mappedAccounts);
          setError(null);
        } else {
          setError(result.error || "Failed to fetch related accounts");
          setAccounts([]);
        }
      } else {
        setError("SharePoint API not available");
        setAccounts([]);
      }
    } catch (err) {
      console.error("Error fetching related accounts:", err);
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-white">Related Accounts and Areas</Label>
        <Button 
          onClick={handleLoad}
          disabled={loading}
          className="bg-primary text-white hover:bg-primary/80"
          data-allow-archival-edit={true}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </>
          ) : (
            "Load Table"
          )}
        </Button>
      </div>
      
      {loading ? (
        <div className="p-8 border border-white/10 rounded-lg bg-black/20 text-center text-white/60">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <p>Loading related accounts...</p>
        </div>
      ) : error ? (
        <div className="p-8 border border-red-500/20 rounded-lg bg-red-500/10 text-center text-red-400">
          <p>Error: {error}</p>
        </div>
      ) : accounts.length === 0 ? (
        <div className="p-8 border border-white/10 rounded-lg bg-black/20 text-center text-white/60">
          {workspaces.length === 0 
            ? "No workspaces linked. Please link workspaces in the Control Details tab."
            : "Click 'Load Table' to fetch related accounts data."}
        </div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-black/20">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/60">PAR</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Note Line ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Note Line</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-white/60">Amount</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account, index) => (
                <tr key={index} className="border-t border-white/10 hover:bg-white/5">
                  <td className="px-4 py-3 text-white">{account.par}</td>
                  <td className="px-4 py-3 text-white">{account.note_line_id || ""}</td>
                  <td className="px-4 py-3 text-white">{account.note_line || ""}</td>
                  <td className="px-4 py-3 text-right text-white">
                    {account.amount.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const ControlSummaryAutomated: React.FC<ControlSummaryAutomatedProps> = ({ data, onChange, controlId }) => {
  const [activePane, setActivePane] = useState<PaneType>("control-details");
  const [sections, setSections] = useState<string[]>([]);
  const [loadingSections, setLoadingSections] = useState(false);
  
  const linkedWorkspaces = data.workspaceLinked || [];

  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Load sections from Sections.json when component mounts
  useEffect(() => {
    const loadSections = async () => {
      setLoadingSections(true);
      try {
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadSectionsJson === 'function') {
          const result = await window.sharePointAPI.downloadSectionsJson();
          
          if (result.success && result.data) {
            // Extract all section names from Planning, Execution, and ConcludingProcedures
            const allSections: string[] = [];
            
            if (result.data.Planning) {
              allSections.push(...Object.keys(result.data.Planning));
            }
            if (result.data.Execution) {
              allSections.push(...Object.keys(result.data.Execution));
            }
            if (result.data.ConcludingProcedures) {
              allSections.push(...Object.keys(result.data.ConcludingProcedures));
            }
            
            // Remove duplicates and sort
            const uniqueSections = Array.from(new Set(allSections)).sort();
            setSections(uniqueSections);
            console.log('Loaded sections from Sections.json:', uniqueSections.length, 'sections');
          } else {
            console.error('Failed to load sections:', result.error);
            setSections([]);
          }
        } else {
          console.warn('SharePoint API not available');
          setSections([]);
        }
      } catch (error) {
        console.error('Error loading sections:', error);
        setSections([]);
      } finally {
        setLoadingSections(false);
      }
    };

    loadSections();
  }, []);

  // Memoize callbacks to prevent Select components from re-rendering unnecessarily
  const handleTypeOfControlChange = useCallback((value: "Direct" | "Indirect" | "GITC") => {
    onChangeRef.current({ typeOfControl: value });
  }, []);

  const handleApproachChange = useCallback((value: "Preventive" | "Detective") => {
    onChangeRef.current({ approach: value });
  }, []);

  const handleControlTypeChange = useCallback((value: string) => {
    onChangeRef.current({ controlType: value as any });
  }, []);

  const handleWorkspaceLinkedChange = useCallback((selected: string[]) => {
    onChangeRef.current({ workspaceLinked: selected });
  }, []);

  // Get stable values for native selects
  const typeOfControlValue = data.typeOfControl || "Direct";
  const approachValue = data.approach || "Preventive";
  const controlTypeValue = data.controlType || "";

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">Control Summary</h2>
      </div>
      <div className="p-6">
        {/* Pane Navigation */}
        <div className="flex space-x-1 bg-black/20 rounded-lg p-1 mb-6">
          <button
            onClick={() => setActivePane("control-details")}
            className={cn(
              "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all",
              activePane === "control-details"
                ? "bg-primary text-white"
                : "text-white/60 hover:text-white hover:bg-white/10"
            )}
          >
            Control Details
          </button>
          <button
            onClick={() => setActivePane("related-accounts")}
            className={cn(
              "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all",
              activePane === "related-accounts"
                ? "bg-primary text-white"
                : "text-white/60 hover:text-white hover:bg-white/10"
            )}
          >
            Related Accounts and Areas
          </button>
        </div>

        {/* Control Details Pane */}
        {activePane === "control-details" && (
          <div className="space-y-6">
            {/* Control ID */}
            <div className="space-y-2">
              <Label className="text-white">Control ID</Label>
              <Input
                value={data.controlId}
                disabled
                className="bg-black/20 text-white/60 cursor-not-allowed border-white/10"
              />
            </div>

            {/* Control Name */}
            <div className="space-y-2">
              <Label className="text-white">Control Name</Label>
              <Input
                value={data.controlName || ""}
                onChange={(e) => onChange({ controlName: e.target.value })}
                className="bg-black/20 text-white border-white/10"
                placeholder="Enter control name..."
              />
            </div>

            {/* Workspace Linked */}
            <div className="space-y-2">
              <Label className="text-white">
                Workspace Linked <span className="text-white/60">(Select all applicable)</span>
              </Label>
              <MultiSelectDropdown
                options={sections}
                value={linkedWorkspaces}
                onChange={handleWorkspaceLinkedChange}
                placeholder="Select workspaces..."
                loading={loadingSections}
              />
            </div>

            {/* Control Description */}
            <RichTextEditor
              label="Control Description"
              value={data.controlDescription}
              onChange={(content) => onChange({ controlDescription: content })}
              placeholder="Enter control description..."
            />

            {/* Control Attributes */}
            <div className="space-y-4 border-t border-white/10 pt-6">
              <h4 className="text-sm font-medium text-white">Control Attributes</h4>

              {/* Type of Control */}
              <div className="space-y-2">
                <Label className="text-white">Type of Control</Label>
                <select
                  value={typeOfControlValue}
                  onChange={(e) => handleTypeOfControlChange(e.target.value as "Direct" | "Indirect" | "GITC")}
                  className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                >
                  <option value="Direct">Direct</option>
                  <option value="Indirect">Indirect</option>
                  <option value="GITC">GITC</option>
                </select>
              </div>

              {/* Nature */}
              <div className="space-y-2">
                <Label className="text-white">Nature</Label>
                <Input
                  value={data.nature}
                  disabled
                  className="bg-black/20 text-white/60 cursor-not-allowed border-white/10"
                />
              </div>

              {/* Approach */}
              <div className="space-y-2">
                <Label className="text-white">Approach</Label>
                <select
                  value={approachValue}
                  onChange={(e) => handleApproachChange(e.target.value as "Preventive" | "Detective")}
                  className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                >
                  <option value="Preventive">Preventive</option>
                  <option value="Detective">Detective</option>
                </select>
              </div>

              {/* Type - Multiple choice dropdown for automated control types */}
              <div className="space-y-2">
                <Label className="text-white">Type</Label>
                <select
                  value={controlTypeValue}
                  onChange={(e) => handleControlTypeChange(e.target.value)}
                  className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#4da3ff]/50"
                >
                  <option value="">Select control type</option>
                  {AUTOMATED_CONTROL_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tailoring Questions */}
            <div className="space-y-4 border-t border-white/10 pt-6">
              <h4 className="text-sm font-medium text-white">Tailoring Questions</h4>

              <div className="flex items-center justify-between">
                <Label className="flex-1 text-white">
                  The engagement team is planning to test the operating effectiveness of the control.
                </Label>
                <Switch
                  checked={data.testOperatingEffectiveness}
                  onCheckedChange={(checked) => onChange({ testOperatingEffectiveness: checked })}
                />
              </div>
            </div>
          </div>
        )}

        {/* Related Accounts Pane */}
        {activePane === "related-accounts" && (
          <div className="space-y-6">
            <RelatedAccountsTable workspaces={linkedWorkspaces} />
          </div>
        )}
      </div>
    </div>
  );
};

